import {
  Building2,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  UsersRound,
} from "lucide-react";

import {
  authorPositionLabel,
  type Manuscript,
  type ManuscriptAffiliation,
  type ManuscriptAuthor,
} from "../domain/manuscript";

type AuthorsEditorProps = {
  manuscript: Manuscript;
  onAddAuthor: () => void;
  onAuthorChange: (id: string, patch: Partial<Omit<ManuscriptAuthor, "id">>) => void;
  onMoveAuthor: (id: string, direction: -1 | 1) => void;
  onCoFirstChange: (id: string, checked: boolean) => void;
  onCoCorrespondingChange: (id: string, checked: boolean) => void;
  onDeleteAuthor: (id: string) => void;
  onAddAffiliation: () => void;
  onAffiliationChange: (
    id: string,
    patch: Partial<Omit<ManuscriptAffiliation, "id">>,
  ) => void;
  onDeleteAffiliation: (id: string) => void;
  onCorrespondenceNoteChange: (value: string) => void;
};

export function AuthorsEditor({
  manuscript,
  onAddAuthor,
  onAuthorChange,
  onMoveAuthor,
  onCoFirstChange,
  onCoCorrespondingChange,
  onDeleteAuthor,
  onAddAffiliation,
  onAffiliationChange,
  onDeleteAffiliation,
  onCorrespondenceNoteChange,
}: AuthorsEditorProps) {
  return (
    <main className="editor-panel">
      <div className="editor-hero">
        <div className="hero-icon">
          <UsersRound size={22} />
        </div>
        <div>
          <span className="eyebrow">AUTHOR BYLINE</span>
          <h1>作者、排序与机构</h1>
          <p>按表格顺序形成署名；作者和机构都可在表格底部继续添加。</p>
        </div>
      </div>

      <section className="editor-card byline-table-card">
        <div className="table-section-heading">
          <div>
            <UsersRound size={17} />
            <span>
              <strong>作者</strong>
              <small>1、2、3… 即最终署名顺序。</small>
            </span>
          </div>
          <span className="table-count">{manuscript.authors.length} 人</span>
        </div>

        <div className="compact-table-scroll">
          <table className="byline-table author-table">
            <thead>
              <tr>
                <th>作者身份</th>
                <th>名字</th>
                <th>机构编号</th>
                <th aria-label="作者操作" />
              </tr>
            </thead>
            <tbody>
              {manuscript.authors.map((author, index) => {
                const position = authorPositionLabel(index);
                const isLast = index === manuscript.authors.length - 1;
                return (
                  <tr key={author.id}>
                    <td className="author-role-column">
                      {index === 0 ? (
                        <div className="author-role-toggles primary-author-role-toggles">
                          <span className="fixed-author-role">一作</span>
                          <label>
                            <input
                              aria-label={`${position} 兼通讯`}
                              checked={author.corresponding}
                              onChange={(event) =>
                                onCoCorrespondingChange(
                                  author.id,
                                  event.target.checked,
                                )
                              }
                              type="checkbox"
                            />
                            兼通讯
                          </label>
                        </div>
                      ) : index === 1 ? (
                        <div className="author-role-toggles primary-author-role-toggles">
                          <label className="fixed-author-role communication-role-tag">
                            <input
                              aria-label={`${position} 通讯`}
                              checked={author.corresponding}
                              onChange={(event) =>
                                onCoCorrespondingChange(
                                  author.id,
                                  event.target.checked,
                                )
                              }
                              type="checkbox"
                            />
                            通讯作者
                          </label>
                          <label>
                            <input
                              aria-label={`${position} 共一`}
                              checked={author.equalContribution}
                              onChange={(event) =>
                                onCoFirstChange(author.id, event.target.checked)
                              }
                              type="checkbox"
                            />
                            共一
                          </label>
                        </div>
                      ) : (
                        <div className="author-role-toggles shared-author-role-toggles">
                          <label>
                            <input
                              aria-label={`${position} 共一`}
                              checked={author.equalContribution}
                              onChange={(event) =>
                                onCoFirstChange(author.id, event.target.checked)
                              }
                              type="checkbox"
                            />
                            共一
                          </label>
                          <label>
                            <input
                              aria-label={`${position} 共通讯`}
                              checked={author.corresponding}
                              onChange={(event) =>
                                onCoCorrespondingChange(
                                  author.id,
                                  event.target.checked,
                                )
                              }
                              type="checkbox"
                            />
                            共通讯
                          </label>
                        </div>
                      )}
                    </td>
                    <td>
                      <div
                        className={`author-contact-inline ${
                          author.corresponding ? "has-email" : "name-only"
                        }`}
                      >
                        <input
                          aria-label={`${position} 姓名`}
                          onChange={(event) =>
                            onAuthorChange(author.id, { name: event.target.value })
                          }
                          placeholder="作者姓名"
                          value={author.name}
                        />
                        {author.corresponding ? (
                          <input
                            aria-label={`${position} 邮箱`}
                            onChange={(event) =>
                              onAuthorChange(author.id, {
                                email: event.target.value,
                              })
                            }
                            placeholder="通讯邮箱"
                            type="email"
                            value={author.email}
                          />
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <fieldset className="table-affiliation-picker">
                        <legend>关联机构</legend>
                        {manuscript.affiliations.length ? (
                          manuscript.affiliations.map(
                            (affiliation, affiliationIndex) => {
                              const checked = author.affiliationIds.includes(
                                affiliation.id,
                              );
                              return (
                                <label
                                  key={affiliation.id}
                                  title={affiliation.name || `机构 ${affiliationIndex + 1}`}
                                >
                                  <input
                                    aria-label={`${position} 关联机构 ${affiliationIndex + 1}`}
                                    checked={checked}
                                    onChange={(event) => {
                                      const next = event.target.checked
                                        ? [
                                            ...new Set([
                                              ...author.affiliationIds,
                                              affiliation.id,
                                            ]),
                                          ]
                                        : author.affiliationIds.filter(
                                            (id) => id !== affiliation.id,
                                          );
                                      onAuthorChange(author.id, {
                                        affiliationIds: next,
                                      });
                                    }}
                                    type="checkbox"
                                  />
                                  {affiliationIndex + 1}
                                </label>
                              );
                            },
                          )
                        ) : (
                          <small>先添加机构</small>
                        )}
                      </fieldset>
                    </td>
                    <td>
                      <div className="table-row-actions">
                        <button
                          aria-label={`上移作者 ${author.name || position}`}
                          disabled={index === 0}
                          onClick={() => onMoveAuthor(author.id, -1)}
                          type="button"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          aria-label={`下移作者 ${author.name || position}`}
                          disabled={isLast}
                          onClick={() => onMoveAuthor(author.id, 1)}
                          type="button"
                        >
                          <ChevronDown size={14} />
                        </button>
                        <button
                          aria-label={`删除作者 ${author.name || position}`}
                          className="danger-icon"
                          onClick={() => onDeleteAuthor(author.id)}
                          type="button"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!manuscript.authors.length ? (
          <p className="table-empty-row">尚未添加作者。</p>
        ) : null}
        <button className="table-add-row" onClick={onAddAuthor} type="button">
          <Plus size={15} /> 添加作者
        </button>

        <div className="table-section-heading affiliation-table-heading">
          <div>
            <Building2 size={17} />
            <span>
              <strong>机构</strong>
              <small>机构同样按 1、2、3… 编号，作者可勾选多个编号。</small>
            </span>
          </div>
          <span className="table-count">{manuscript.affiliations.length} 个</span>
        </div>

        <div className="compact-table-scroll">
          <table className="byline-table affiliation-table">
            <thead>
              <tr>
                <th>序号</th>
                <th>机构名称</th>
                <th aria-label="机构操作" />
              </tr>
            </thead>
            <tbody>
              {manuscript.affiliations.map((affiliation, index) => (
                <tr key={affiliation.id}>
                  <td className="row-number-cell">
                    <span className="position-badge">{index + 1}</span>
                  </td>
                  <td>
                    <div className="affiliation-name-cell">
                      <input
                        aria-label={`机构 ${index + 1} 完整名称`}
                        onChange={(event) =>
                          onAffiliationChange(affiliation.id, {
                            name: event.target.value,
                          })
                        }
                        placeholder="Department, University, City, Country"
                        value={affiliation.name}
                      />
                      <details className="affiliation-details">
                        <summary>ROR / 机构标识</summary>
                        <input
                          aria-label={`机构 ${index + 1} ROR`}
                          onChange={(event) =>
                            onAffiliationChange(affiliation.id, {
                              ror: event.target.value,
                            })
                          }
                          placeholder="https://ror.org/...（可选）"
                          value={affiliation.ror}
                        />
                      </details>
                    </div>
                  </td>
                  <td>
                    <button
                      aria-label={`删除机构 ${index + 1}`}
                      className="danger-icon"
                      onClick={() => onDeleteAffiliation(affiliation.id)}
                      type="button"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!manuscript.affiliations.length ? (
          <p className="table-empty-row">尚未添加机构。</p>
        ) : null}
        <button className="table-add-row" onClick={onAddAffiliation} type="button">
          <Plus size={15} /> 添加机构
        </button>

        <label className="correspondence-note-field compact-note-field">
          <span className="field-label">
            通讯地址、电话或期刊补充信息 <small>可选</small>
          </span>
          <textarea
            onChange={(event) => onCorrespondenceNoteChange(event.target.value)}
            placeholder="Postal address: ...\nTelephone: ..."
            rows={3}
            value={manuscript.metadata.correspondence}
          />
          <small className="field-help">
            通讯邮箱在对应作者姓名右侧填写；ICMJE 题名页还建议列出通讯电话。
          </small>
        </label>
      </section>
    </main>
  );
}
